import { Fragment, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import TableCell from "@mui/material/TableCell";
import TableRowMui from "@mui/material/TableRow";

import { colors } from "@dotnet/telematics-design-system";

import InlineActionIconButton from "components/DesignComponents/Button/InlineActionIconButton";
import Checkbox from "components/DesignComponents/Checkbox";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import AuthContext from "context/auth/authContext";
import TableContext from "context/table/tableContext";
import { isUserHimself } from "shared/functions";
import { useCompanyLevelSettings } from "shared/hooks/useCompanyLevelSettings";
import { tableColumnDropShadow } from "shared/styles/styleFunctions";
import { TABLE_NAMES } from "shared/types/enums";
import { Column, RenderConditionOptions } from "shared/types/interfaces";

import TableColumn from "./TableColumn";
import { TableRowProps } from "./types";

const TableRow = ({
    data,
    isChecked,
    onCheckboxChange,
    isDisabled,
    scrollHorizontal
}: TableRowProps) => {
    const { blue100, blue200, blue700, gray50, gray200, green50, white } =
        colors;

    const { user } = useContext(AuthContext);

    const {
        tableName,
        isNotSelectable,
        isTableLoading,
        idType,
        hasDetails,
        columns,
        inlineActions,
        detailsData,
        outerDetailsData,
        blinkedRows,
        removeBlinkedRow,
        setDetailsData
    } = useContext(TableContext);

    const { t } = useTranslation();
    const { isFeatureSettingsRestricted } = useCompanyLevelSettings();

    const [isInlineBtnLoading, setIsInlineBtnLoading] = useState(false);

    const boxShadowBottom = `inset 0px -1px 0px ${gray200}`;
    const checkedLeftBorder = `inset 2px 0px 0px ${blue700}`;

    const id = data[idType];
    const isSelected = detailsData && id === detailsData[idType];

    const handleClick = () => {
        if (hasDetails && !isSelected) {
            setDetailsData(data);
        }
    };

    const getCheckboxTooltipTitle = () => {
        if (isDisabled) {
            switch (tableName) {
                case TABLE_NAMES.Users: {
                    if (isUserHimself(user!.id, data.id)) {
                        return t("Table##user cannot delete himself");
                    }

                    return t("Table##user cannot be deleted");
                }
                case TABLE_NAMES.Roles:
                    return t("Table##checkbox disabled##role");
                default:
                    return undefined;
            }
        }

        return undefined;
    };

    return (
        <TableRowMui
            onAnimationEnd={() => removeBlinkedRow(id)}
            onClick={handleClick}
            selected={isSelected}
            css={css({
                cursor: isTableLoading || !hasDetails ? "auto" : "pointer",

                td: {
                    background: isChecked ? blue100 : white,
                    animationName: blinkedRows.includes(id) ? "blink" : "unset",
                    animationDuration: "0.5s",
                    animationTimingFunction: "ease-out",

                    "@keyframes blink": {
                        "25%": {
                            background: green50
                        },

                        "75%": {
                            background: green50
                        }
                    }
                },

                "&:hover td": {
                    background: isChecked ? blue200 : gray50
                }
            })}
        >
            {/* Checkbox */}
            {!isNotSelectable && (
                <TableCell
                    css={css({
                        position: "sticky",
                        left: "0",
                        boxShadow: isChecked
                            ? `${checkedLeftBorder}, ${boxShadowBottom}`
                            : boxShadowBottom,

                        "& > div": {
                            height: "24px"
                        }
                    })}
                >
                    <TooltipGeneral
                        title={getCheckboxTooltipTitle()}
                        placement={TOOLTIP_PLACEMENT.Top}
                    >
                        <div>
                            <Checkbox
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={onCheckboxChange}
                                onClick={e => e.stopPropagation()}
                            />
                        </div>
                    </TooltipGeneral>
                </TableCell>
            )}

            {/* Column values */}
            {columns.map(
                ({
                    name,
                    resource,
                    isActive,
                    columnIdentifier,
                    isSticky,
                    isValueTranslatable,
                    customDateTimeFormat,
                    restriction
                }: Column) => {
                    const isRestricted =
                        isFeatureSettingsRestricted(restriction);

                    return (
                        isActive && (
                            <TableColumn
                                key={name}
                                data={data}
                                name={name}
                                resource={resource}
                                isRestricted={isRestricted}
                                columnIdentifier={columnIdentifier}
                                isSticky={isSticky}
                                isTranslatable={isValueTranslatable}
                                customDateTimeFormat={customDateTimeFormat}
                                scrollHorizontal={scrollHorizontal}
                            />
                        )
                    );
                }
            )}

            {/* Inline actions */}
            {inlineActions.length > 0 && (
                <TableCell
                    onClick={e => e.stopPropagation()}
                    sx={[
                        {
                            position: "sticky",
                            right: "0",
                            padding: "0 !important",
                            overflowX: "unset !important",
                            background: `${white} !important`,
                            boxShadow: boxShadowBottom,
                            cursor: "auto"
                        },
                        tableColumnDropShadow(scrollHorizontal)
                    ]}
                >
                    <div
                        css={css({
                            display: "flex",
                            justifyContent: "end",
                            minWidth: "72px",

                            "& > span + span": {
                                marginLeft: "12px"
                            },

                            "& > span:first-of-type": {
                                marginLeft: "18px"
                            },

                            "& > span:last-of-type": {
                                marginRight: "18px"
                            }
                        })}
                    >
                        <div
                            css={css({
                                position: "absolute",
                                top: "0",
                                bottom: "0",
                                left: "0",
                                width: "1px",
                                background: gray200
                            })}
                        />

                        {inlineActions.map(
                            ({
                                title,
                                icon,
                                hasLoader,
                                clickAction,
                                renderConditions,
                                getDisabledText
                            }) => {
                                const Icon = icon;

                                const getRender = (
                                    renderFunction:
                                        | ((
                                              options: RenderConditionOptions
                                          ) => void)
                                        | undefined
                                ) =>
                                    renderFunction === undefined ||
                                    renderFunction({
                                        data,
                                        outerDetailsData:
                                            outerDetailsData?.detailsData ??
                                            null,
                                        user
                                    });

                                const render = getRender(renderConditions);

                                const disabledText = getDisabledText?.({
                                    data,
                                    user
                                });

                                const onClick = () => {
                                    hasLoader && setIsInlineBtnLoading(true);

                                    clickAction(data, () =>
                                        setIsInlineBtnLoading(false)
                                    );
                                };

                                return (
                                    <Fragment key={title}>
                                        {render ? (
                                            <InlineActionIconButton
                                                key={title}
                                                title={t(`General##${title}`)}
                                                icon={<Icon />}
                                                onButtonClick={onClick}
                                                disabledText={disabledText}
                                                isLoading={
                                                    hasLoader &&
                                                    isInlineBtnLoading
                                                }
                                            />
                                        ) : null}
                                    </Fragment>
                                );
                            }
                        )}
                    </div>
                </TableCell>
            )}
        </TableRowMui>
    );
};

export default TableRow;
